import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import BasePage from '../../components/Layout/BasePage';
import MasterDetailLayout from '../../components/Layout/MasterDetailLayout';
import { renderTabLabel as tabLabel } from '../../components/Layout/tabLabel';
import useUrlTab from '../../hooks/useUrlTab';
import ProductionOverview from './ProductionOverview';
import ProductionOrders from './ProductionOrders';
import Machines from '../Machines/Machines';
import MachineSchedules from '../Machines/MachineSchedules';
import Workflows from '../Workflows/Workflows';
import Tasks from '../Tasks/Tasks';

function ProductionPage({ initialDetailTarget, onDetailConsumed }) {
  const { t } = useTranslation('production');
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Check for orderId and creation requests in the URL params
  const urlOrderId = searchParams.get('orderId');
  const createFromSalesOrder = searchParams.get('createFromSalesOrder');
  const purpose = searchParams.get('purpose');
  const missingValue = searchParams.get('missing_value');
  const quoteId = searchParams.get('quote_id');

  // Active tab in the URL (?tab=), remembered per module
  const { activeTab, setActiveTab } = useUrlTab(
    'production', ['overview', 'production', 'machines', 'schedules', 'workflows', 'tasks'], 'overview'
  );
  const [scheduleEditMode, setScheduleEditMode] = useState(false);
  // Production order to open ({ id, innerTab, nonce }). ProductionOrders opens it and calls
  // onOpenOrderConsumed, so a later remount of the tab shows the list instead of reopening it.
  const [openOrder, setOpenOrder] = useState(() => (urlOrderId ? { id: urlOrderId, innerTab: null, nonce: Date.now() } : null));
  const [returnToPO, setReturnToPO] = useState(null);
  // Task to open in the Tasks tab ({ id, nonce }); the nonce reopens it when Tasks is already mounted
  const [initialTask, setInitialTask] = useState(null);
  const [salesOrderData, setSalesOrderData] = useState(null);
  const [stockReplenishmentData, setStockReplenishmentData] = useState(null);

  // Handle detail target from App.js (cross-module navigation)
  useEffect(() => {
    if (!initialDetailTarget) return;

    if (initialDetailTarget.type === 'productionOrder') {
      setActiveTab('production');
      setOpenOrder({ id: initialDetailTarget.id, innerTab: initialDetailTarget.innerTab || null, nonce: Date.now() });
    } else if (initialDetailTarget.type === 'productionOrdersList') {
      setActiveTab('production');
    } else if (initialDetailTarget.type === 'task') {
      setInitialTask({ id: initialDetailTarget.id, nonce: Date.now() });
      setActiveTab('tasks');
      // Where the task was opened from (production order or dashboard); reset so a stale origin is not reused
      setReturnToPO(location.state?.returnTo || null);
    }

    if (onDetailConsumed) onDetailConsumed();
  }, [initialDetailTarget]);

  // Handle URL parameters on mount (the tab itself comes from useUrlTab; switching tabs must not
  // reopen ?orderId= or re-run a creation request)
  useEffect(() => {
    if (urlOrderId) {
      setOpenOrder({ id: urlOrderId, innerTab: null, nonce: Date.now() });
    }
    // Handle creating from sales order
    if (createFromSalesOrder) {
      // Read from sessionStorage
      const storedData = sessionStorage.getItem('salesOrderDataForProduction');
      if (storedData) {
        try {
          const salesData = JSON.parse(storedData);
          setSalesOrderData(salesData);
          // Clear sessionStorage after reading
          sessionStorage.removeItem('salesOrderDataForProduction');
          // Don't clear state immediately - let the child component use it first
          // The ProductionOrders component will handle clearing it
        } catch (e) {
          console.error('Error parsing sales order data:', e);
        }
      }
    }

    // Handle stock replenishment creation
    if (purpose === 'stock_replenishment') {
      setStockReplenishmentData({
        purpose: 'STOCK_REPLENISHMENT',
        missing_value: missingValue,
        quote_id: quoteId,
        notes: `Stock replenishment order - Missing value: $${missingValue} (from Quote ${quoteId})`
      });
    }
  }, [urlOrderId, createFromSalesOrder, purpose, missingValue, quoteId]);


  const renderTabLabel = (translationKey, shortcutChar) => tabLabel(t(`tabs.${translationKey}`), shortcutChar);

  const tabs = [
    {
      id: 'overview',
      label: renderTabLabel('overview', 'o'),
      shortcut: 'o',
      hasSubTabs: false
    },
    {
      id: 'production',
      label: renderTabLabel('production', 'd'),
      shortcut: 'd',
      hasSubTabs: false
    },
    {
      id: 'machines',
      label: renderTabLabel('machines', 'm'),
      shortcut: 'm',
      hasSubTabs: false
    },
    {
      id: 'schedules',
      label: renderTabLabel('schedules', 's'),
      shortcut: 's',
      hasSubTabs: false
    },
    {
      id: 'workflows',
      label: renderTabLabel('workflows', 'w'),
      shortcut: 'w',
      hasSubTabs: false
    },
    {
      id: 'tasks',
      label: renderTabLabel('tasks', 't'),
      shortcut: 't',
      hasSubTabs: false
    }
  ];



  // Configuration object for MasterDetailLayout
  const config = {
    tabs,
    entities: {},
    toolbars: {
      overview: null,
      production: null,
      machines: null,
      schedules: (
        !scheduleEditMode ? (
          <>
            <button
              onClick={() => {
                if (window.newScheduleHandler) {
                  window.newScheduleHandler();
                }
              }}
              className="icon-btn"
              title={t('toolbar.titles.newSchedule')}
            >
              ➕
            </button>
            <button
              onClick={() => setScheduleEditMode(true)}
              className="icon-btn"
              title={t('toolbar.titles.editMode')}
            >
              ✏️
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => {
                if (window.scheduleSaveHandler) {
                  window.scheduleSaveHandler();
                }
              }}
              className="icon-btn"
              title={t('toolbar.titles.save')}
            >
              💾
            </button>
            <button
              onClick={() => setScheduleEditMode(false)}
              className="icon-btn"
              title={t('toolbar.titles.exitEditMode')}
            >
              ❌
            </button>
          </>
        )
      ),
      workflows: null,
      tasks: null
    }
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="tab-content with-padding">
            <ProductionOverview />
          </div>
        );

      case 'production':
        const viewMode = openOrder ? 'view' : (salesOrderData || stockReplenishmentData) ? 'create' : 'list';
        return <ProductionOrders
          openOrder={openOrder}
          onOpenOrderConsumed={() => setOpenOrder(null)}
          initialViewMode={viewMode}
          salesOrderData={salesOrderData}
          stockReplenishmentData={stockReplenishmentData}
        />;

      case 'machines':
        return <Machines />;

      case 'schedules':
        return (
          <MachineSchedules
            editMode={scheduleEditMode}
            onEditModeChange={setScheduleEditMode}
          />
        );

      case 'workflows':
        return <Workflows />;

      case 'tasks':
        return <Tasks initialTask={initialTask} returnToPO={returnToPO} onReturnConsumed={() => setReturnToPO(null)} />;

      default:
        return null;
    }
  };

  return (
    <BasePage title={t('title')} className="production-page">
      <MasterDetailLayout
        config={config}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {renderTabContent()}
      </MasterDetailLayout>
    </BasePage>
  );
}

export default ProductionPage;